import React from 'react';
import { ALERT_RULES_MOCK, OPERATIONAL_NOTIFICATIONS_MOCK } from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import { saIsRealNotificationsMode } from './mvp-config.jsx';
import { ApiErrorState, EmptyState, SkeletonTable } from './services.jsx';
import { NotifSeverityBadge } from './tenant-status.jsx';
// Notificações Operacionais — Super Admin Tezus
// Inbox, Críticas, Regras de alerta, Histórico, Preferências

const { useState: useStateNO, useEffect: useEffectNO, useMemo: useMemoNO } = React;

// ── Notification Drawer ───────────────────────────────────────────────────────
const NotifDetailDrawer = ({ open, notif, onClose, onAction, onToast }) => {
  if (!open || !notif) return null;

  const typeLabel = { tenant: 'Tenant', billing: 'Billing', provisioning: 'Provisionamento', security: 'Segurança', integration: 'Integração', observability: 'Observabilidade', repair: 'Repair', system: 'Sistema' };
  const typeColor = { tenant: '#1d4ed8', billing: '#b45309', provisioning: '#7c3aed', security: '#EC2A58', integration: '#0f7a3e', observability: '#1B3D60', repair: '#2e7d7d', system: '#6b7691' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.4)', backdropFilter: 'blur(4px)', zIndex: 900, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: 460, background: 'var(--bg-elevated)', height: '100%', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <NotifSeverityBadge severity={notif.severity} />
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, background: (typeColor[notif.type] || '#6b7691') + '18', color: typeColor[notif.type] || '#6b7691', fontSize: 11, fontWeight: 700 }}>
                {typeLabel[notif.type] || notif.type}
              </span>
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.4 }}>{notif.title}</div>
          </div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{notif.body}</div>

          {/* Metadata */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { label: 'Tenant',        value: notif.tenant || '—' },
              { label: 'Criado em',     value: notif.created_at },
              { label: 'Lido em',       value: notif.read_at || 'Não lido' },
              { label: 'Status',        value: notif.status === 'resolved' ? '✓ Resolvido' : 'Em aberto' },
              { label: 'Responsável',   value: notif.assigned_to || 'Não atribuído' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.label}</span>
                <span style={{ fontSize: 12, fontWeight: 500 }}>{r.value}</span>
              </div>
            ))}
          </div>

          {/* Payload técnico */}
          {notif.payload && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Payload técnico</div>
              <pre style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', margin: 0, overflowX: 'auto', lineHeight: 1.6 }}>
                {JSON.stringify(notif.payload, null, 2)}
              </pre>
            </div>
          )}

          {/* Ações recomendadas */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Ações</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {notif.status !== 'resolved' && (
                <>
                  {notif.tenant_id && <button className="btn" style={{ justifyContent: 'flex-start', gap: 10 }} onClick={() => { onAction('cockpit', notif); onClose(); }}><Icon name="store" size={14} />Abrir Tenant Cockpit</button>}
                  {!saIsRealNotificationsMode() && (
                    <button className="btn" style={{ justifyContent: 'flex-start', gap: 10 }} onClick={() => { onAction('assign', notif); onClose(); }}><Icon name="users" size={14} />Atribuir responsável</button>
                  )}
                  <button className="btn primary" style={{ justifyContent: 'flex-start', gap: 10 }} onClick={() => { onAction('resolve', notif); onClose(); }}><Icon name="check" size={14} />Marcar como resolvido</button>
                </>
              )}
              <button className="btn ghost" style={{ justifyContent: 'flex-start', gap: 10 }} onClick={() => { onAction('silence', notif); onClose(); }}><Icon name="bell" size={14} />Silenciar notificação</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Alert Rule Modal ──────────────────────────────────────────────────────────
const AlertRuleModal = ({ open, onClose, onSave }) => {
  const [form, setForm] = useStateNO({ name: '', type: 'billing', condition: '', severity: 'warning', channel: ['inbox'], enabled: true });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const toggleChannel = (ch) => setForm(f => ({
    ...f,
    channel: f.channel.includes(ch) ? f.channel.filter(c => c !== ch) : [...f.channel, ch],
  }));

  useEffectNO(() => { if (!open) setForm({ name: '', type: 'billing', condition: '', severity: 'warning', channel: ['inbox'], enabled: true }); }, [open]);

  if (!open) return null;
  const inputSt = { width: '100%', height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
  const labelSt = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 500, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Nova regra de alerta</div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div><label style={labelSt}>Nome da regra *</label><input style={inputSt} value={form.name} onChange={set('name')} placeholder="Ex: Tenant provisionando > 10 min" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelSt}>Tipo</label>
              <select value={form.type} onChange={set('type')} className="select" style={{ width: '100%' }}>
                {[['billing','Billing'],['tenant','Tenant'],['provisioning','Provisionamento'],['security','Segurança'],['observability','Observabilidade'],['integration','Integração']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelSt}>Severidade</label>
              <select value={form.severity} onChange={set('severity')} className="select" style={{ width: '100%' }}>
                {[['info','Info'],['warning','Alerta'],['critical','Crítico'],['security','Segurança']].map(([v,l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>
          <div><label style={labelSt}>Condição</label><input style={inputSt} value={form.condition} onChange={set('condition')} placeholder="Ex: provisioning_time > 10m" /></div>
          <div>
            <label style={labelSt}>Canais de notificação</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['inbox','Inbox'],['email','E-mail'],['slack','Slack/Webhook'],['internal','Interno']].map(([ch, label]) => (
                <label key={ch} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', borderRadius: 8, background: form.channel.includes(ch) ? 'var(--info-bg)' : 'var(--bg-subtle)', border: '1px solid ' + (form.channel.includes(ch) ? 'var(--info)' : 'var(--border)'), fontSize: 13 }}>
                  <input type="checkbox" checked={form.channel.includes(ch)} onChange={() => toggleChannel(ch)} style={{ width: 13, height: 13, accentColor: 'var(--info)' }} />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.enabled} onChange={set('enabled')} style={{ width: 14, height: 14, accentColor: 'var(--primary)' }} />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Regra ativa</span>
          </label>
        </div>
        <div style={{ padding: '12px 24px', background: 'var(--bg-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={() => { onSave(form); onClose(); }} disabled={!form.name || !form.condition}>
            <Icon name="check" size={13} />Criar regra
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Card dedicado para notificações de SLA/Ticket ─────────────────────────────
const SLANotifCard = ({ n, idx, total, onMarkRead, onResolve, onOpenTicket }) => {
  const priColor  = { urgente: 'var(--danger)', alta: 'var(--warning)', media: 'var(--info)', baixa: 'var(--text-muted)' };
  const priLabel  = { urgente: 'Urgente', alta: 'Alta', media: 'Média', baixa: 'Baixa' };
  const slaColor  = n.sla_status === 'vencido' ? 'var(--danger)' : n.sla_status === 'vencendo' ? 'var(--warning)' : n.sla_status === 'novo' ? 'var(--info)' : 'var(--text-muted)';
  const slaBg     = n.sla_status === 'vencido' ? 'var(--danger-bg)' : n.sla_status === 'vencendo' ? 'var(--warning-bg)' : n.sla_status === 'novo' ? 'var(--info-bg)' : 'var(--bg-subtle)';
  const slaLabel  = { vencido: 'SLA VENCIDO', vencendo: 'SLA VENCENDO', novo: 'NOVO TICKET', normal: 'SLA OK' };
  const isUnread  = !n._read && !n.read_at;

  return (
    <div style={{
      padding: '14px 16px',
      borderBottom: idx < total - 1 ? '1px solid var(--border)' : 'none',
      background: isUnread ? 'color-mix(in srgb, var(--danger) 3%, var(--bg-elevated))' : 'var(--bg-elevated)',
      display: 'flex', gap: 12, alignItems: 'flex-start',
      borderLeft: `3px solid ${slaColor}`,
    }}>
      {/* Ícone */}
      <div style={{ width: 36, height: 36, borderRadius: 10, background: slaBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="messageCircle" size={16} style={{ color: slaColor }} />
      </div>
      {/* Conteúdo */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Linha 1: título + badges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
          {isUnread && <div style={{ width: 7, height: 7, borderRadius: '50%', background: slaColor, flexShrink: 0 }} />}
          <span style={{ fontWeight: isUnread ? 700 : 500, fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0 }}>{n.title}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 20, background: slaBg, color: slaColor, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
            <Icon name={n.sla_status === 'vencido' ? 'alert' : 'clock'} size={9}/>{slaLabel[n.sla_status] || 'SLA'} · {n.sla_time}
          </span>
          {n.priority && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 20, border: `1px solid ${priColor[n.priority] || 'var(--border)'}`, color: priColor[n.priority] || 'var(--text-muted)', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
              {priLabel[n.priority] || n.priority}
            </span>
          )}
        </div>
        {/* Linha 2: metadados */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 5, flexWrap: 'wrap', alignItems: 'center' }}>
          {n.ticket_id && (
            <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: slaColor, fontWeight: 700 }}>Ticket {n.ticket_id}</span>
          )}
          {n.tenant && (
            <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 500 }}>· {n.tenant}</span>
          )}
          {n.assigned_to
            ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {n.assigned_to}</span>
            : <span style={{ fontSize: 11, color: 'var(--warning)', fontWeight: 600 }}>· Sem responsável</span>
          }
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{n.created_at}</span>
        </div>
        {/* Corpo */}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4, marginBottom: 8 }}>{n.body}</div>
        {/* Ações */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {n.ticket_id && (
            <button className="btn sm primary" style={{ fontSize: 11, height: 26 }} onClick={() => onOpenTicket(n)}>
              <Icon name="messageCircle" size={10}/>Abrir ticket {n.ticket_id}
            </button>
          )}
          {n.status !== 'resolved' && (
            <button className="btn sm" style={{ fontSize: 11, height: 26 }} onClick={() => onResolve(n.id)}>
              <Icon name="check" size={10}/>Resolver
            </button>
          )}
          {isUnread && (
            <button className="btn sm ghost" style={{ fontSize: 11, height: 26 }} onClick={() => onMarkRead(n.id)}>
              Marcar lida
            </button>
          )}
          {n.status === 'resolved' && (
            <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="check" size={10}/>Resolvido
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Tab: Inbox ────────────────────────────────────────────────────────────────
const TabInbox = ({ notifs, setNotifs, onToast, onNav, filter }) => {
  const [selected, setSelected] = useStateNO(null);
  const store = window._notifStore;

  const filtered = useMemoNO(() => {
    let base = notifs;
    if (filter === 'unread')     base = base.filter(n => !n._read && !n.read_at);
    else if (filter === 'critical') base = base.filter(n => n.severity === 'critical' || n.severity === 'security');
    else if (filter === 'sla_ticket') base = base.filter(n => n.type === 'sla_ticket');
    else if (filter !== 'all')   base = base.filter(n => n.type === filter);
    return base;
  }, [notifs, filter]);

  const markRead = async (id) => {
    try {
      if (store) await store.markRead(id);
      else setNotifs(ns => ns.map(n => n.id === id ? { ...n, _read: true, read_at: 'Agora' } : n));
    } catch (e) { onToast && onToast(e?.message || 'Erro ao marcar como lida'); }
  };
  const resolve  = async (id) => {
    try {
      if (store) await store.resolve(id);
      else setNotifs(ns => ns.map(n => n.id === id ? { ...n, status: 'resolved', _read: true, read_at: n.read_at || 'Agora' } : n));
      onToast('Notificação resolvida');
    } catch (e) { onToast && onToast(e?.message || 'Erro ao resolver'); }
  };

  const handleOpenTicket = (n) => {
    markRead(n.id);
    onNav({ page: 'tickets', openTicketId: n.ticket_id });
  };

  const handleAction = (action, notif) => {
    if (action === 'cockpit' && notif.tenant_id) onNav({ page: 'lojas' });
    if (action === 'resolve') resolve(notif.id);
    if (action === 'silence') {
      if (store) store.resolve(notif.id);
      else setNotifs(ns => ns.filter(n => n.id !== notif.id));
      onToast('Notificação silenciada');
    }
    if (action === 'assign') onToast('Atribuição registrada');
  };

  const typeLabel = { tenant: 'Tenant', billing: 'Billing', provisioning: 'Prov.', security: 'Seg.', integration: 'Integração', observability: 'Obs.', repair: 'Repair', system: 'Sistema', sla_ticket: 'SLA/Ticket' };
  const typeColor = { tenant: '#1d4ed8', billing: '#b45309', provisioning: '#7c3aed', security: '#EC2A58', integration: '#0f7a3e', observability: '#1B3D60', repair: '#2e7d7d', system: '#6b7691', sla_ticket: 'var(--danger)' };

  return (
    <div>
      {filtered.length === 0 && <EmptyState icon="bell" title="Sem notificações" description={filter === 'unread' ? 'Todas as notificações foram lidas.' : 'Nenhuma notificação nesta categoria.'} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, background: 'var(--bg-elevated)', borderRadius: 12, border: filtered.length > 0 ? '1px solid var(--border)' : 'none', overflow: 'hidden' }}>
        {filtered.map((n, idx) => {
          // Renderização dedicada para sla_ticket
          if (n.type === 'sla_ticket') {
            return (
              <SLANotifCard
                key={n.id} n={n} idx={idx} total={filtered.length}
                onMarkRead={markRead}
                onResolve={resolve}
                onOpenTicket={handleOpenTicket}
              />
            );
          }
          const color = typeColor[n.type] || '#6b7691';
          const isUnread = !n._read && !n.read_at;
          return (
            <div key={n.id} style={{ padding: '14px 16px', borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', gap: 12, background: isUnread ? 'color-mix(in srgb, var(--brand-navy) 3%, var(--bg-elevated))' : 'var(--bg-elevated)', cursor: 'pointer', transition: 'background 100ms' }}
              onClick={() => { markRead(n.id); setSelected(n); }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = isUnread ? 'color-mix(in srgb, var(--brand-navy) 3%, var(--bg-elevated))' : 'var(--bg-elevated)'}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={n.severity === 'critical' || n.severity === 'security' ? 'alert' : n.type === 'billing' ? 'cardCoin' : n.type === 'provisioning' ? 'refresh' : n.type === 'repair' ? 'wrench' : 'bell'} size={16} style={{ color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  {isUnread && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand-navy)', flexShrink: 0 }} />}
                  <span style={{ fontWeight: isUnread ? 700 : 500, fontSize: 13, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                  <NotifSeverityBadge severity={n.severity} />
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 20, background: color + '18', color, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{typeLabel[n.type]}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{n.created_at}</span>
                  {n.tenant && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {n.tenant}</span>}
                  {n.assigned_to && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· Responsável: {n.assigned_to}</span>}
                  <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, background: n.status === 'resolved' ? '#e8f5ee' : 'var(--bg-subtle)', color: n.status === 'resolved' ? '#0f7a3e' : 'var(--text-muted)', fontSize: 10, fontWeight: 700 }}>
                    {n.status === 'resolved' ? '✓ Resolvido' : 'Em aberto'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                {n.status !== 'resolved' && (
                  <button className="icon-action" title="Marcar como resolvido" onClick={() => resolve(n.id)} style={{ width: 28, height: 28 }}><Icon name="check" size={13} /></button>
                )}
                <button className="icon-action" title="Ver detalhes" onClick={() => { markRead(n.id); setSelected(n); }} style={{ width: 28, height: 28 }}><Icon name="more" size={13} /></button>
              </div>
            </div>
          );
        })}
      </div>
      <NotifDetailDrawer open={!!selected} notif={selected} onClose={() => setSelected(null)} onAction={handleAction} onToast={onToast} />
    </div>
  );
};

// ── Tab: Regras de alerta ─────────────────────────────────────────────────────
const TabRegrasAlerta = ({ onToast }) => {
  const [rules, setRules] = useStateNO(ALERT_RULES_MOCK || []);
  const [newModal, setNewModal] = useStateNO(false);
  const toggle = (id) => setRules(rs => rs.map(r => r.id === id ? { ...r, status: r.status === 'active' ? 'inactive' : 'active' } : r));

  const typeColor = { billing: '#b45309', tenant: '#1d4ed8', provisioning: '#7c3aed', security: '#EC2A58', observability: '#1B3D60', integration: '#0f7a3e' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <button className="btn primary" onClick={() => setNewModal(true)}><Icon name="plus" size={13} />Nova regra</button>
      </div>
      <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-subtle)' }}>
            <tr>{['Nome da regra', 'Tipo', 'Condição', 'Severidade', 'Canais', 'Status', 'Ações'].map(h => (
              <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'} onMouseLeave={e => e.currentTarget.style.background = ''}>
                <td style={{ padding: '10px 14px', fontWeight: 500, fontSize: 13 }}>{r.name}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 20, background: (typeColor[r.type] || '#6b7691') + '18', color: typeColor[r.type] || '#6b7691', fontSize: 11, fontWeight: 700 }}>{r.type}</span>
                </td>
                <td style={{ padding: '10px 14px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.condition}</td>
                <td style={{ padding: '10px 14px' }}><NotifSeverityBadge severity={r.severity} /></td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{r.channel.join(', ')}</td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: r.status === 'active' ? '#e8f5ee' : '#f4f5f7', color: r.status === 'active' ? '#0f7a3e' : '#6b7691', fontSize: 11, fontWeight: 600 }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: r.status === 'active' ? '#0f7a3e' : '#6b7691' }} />
                    {r.status === 'active' ? 'Ativa' : 'Inativa'}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="icon-action" title={r.status === 'active' ? 'Desativar' : 'Ativar'} onClick={() => toggle(r.id)}><Icon name={r.status === 'active' ? 'suspend' : 'play'} size={13} /></button>
                    <button className="icon-action" title="Editar" onClick={() => onToast('Editor de regras em breve')}><Icon name="edit" size={13} /></button>
                    <button className="icon-action danger" title="Excluir" onClick={() => { setRules(rs => rs.filter(x => x.id !== r.id)); onToast('Regra removida'); }}><Icon name="trash" size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AlertRuleModal open={newModal} onClose={() => setNewModal(false)} onSave={(rule) => {
        setRules(rs => [...rs, { ...rule, id: 'ar_' + Date.now() }]);
        onToast(`Regra "${rule.name}" criada`);
      }} />
    </div>
  );
};

// ── Tab: Preferências ─────────────────────────────────────────────────────────
const TabPreferencias = ({ onToast }) => {
  const [prefs, setPrefs] = useStateNO({ billing: true, security: true, provisioning: true, observability: false, integrations: true, quiet_hours: false, quiet_start: '22:00', quiet_end: '08:00', digest: 'daily' });
  const set = k => e => setPrefs(p => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const labelSt = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 };
  const inputSt = { height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };

  const togglePref = (key) => ({ label, desc }) => (
    <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
      <input type="checkbox" checked={prefs[key]} onChange={set(key)} style={{ width: 14, height: 14, marginTop: 2, accentColor: 'var(--primary)' }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{desc}</div>
      </div>
    </label>
  );

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Tipos de notificação</div>
        {togglePref('billing')({ label: 'Billing e inadimplência', desc: 'Faturas vencidas, pagamentos falhados, tenants inadimplentes' })}
        {togglePref('security')({ label: 'Segurança e acessos', desc: 'Sessões suspeitas, tentativas de login, tokens comprometidos' })}
        {togglePref('provisioning')({ label: 'Provisionamento', desc: 'Falhas, timeouts e re-provisionamentos de tenants' })}
        {togglePref('observability')({ label: 'Observabilidade', desc: 'Alertas de serviços, alto uso de recursos, erros 5xx' })}
        {togglePref('integrations')({ label: 'Integrações', desc: 'Falhas em tokens, webhooks e sincronizações' })}
      </div>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Horário silencioso</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={prefs.quiet_hours} onChange={set('quiet_hours')} style={{ width: 14, height: 14, accentColor: 'var(--primary)' }} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>Silenciar notificações fora do horário comercial</span>
        </label>
        {prefs.quiet_hours && (
          <div style={{ display: 'flex', gap: 12, paddingLeft: 24 }}>
            <div><label style={labelSt}>Das</label><input type="time" value={prefs.quiet_start} onChange={set('quiet_start')} style={{ ...inputSt, width: 120 }} /></div>
            <div><label style={labelSt}>Até</label><input type="time" value={prefs.quiet_end} onChange={set('quiet_end')} style={{ ...inputSt, width: 120 }} /></div>
          </div>
        )}
      </div>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Resumo diário</div>
        <div>
          <label style={labelSt}>Frequência do resumo</label>
          <select value={prefs.digest} onChange={set('digest')} className="select">
            <option value="never">Nunca</option>
            <option value="daily">Diário</option>
            <option value="weekly">Semanal</option>
          </select>
        </div>
      </div>
      <button className="btn primary" onClick={() => onToast('Preferências salvas')}>Salvar preferências</button>
    </div>
  );
};

// ── Página principal ─────────────────────────────────────────────────────────
const NotificacoesOperacionaisPage = ({ onToast, onNav }) => {
  const store = window._notifStore;
  const useRealNotif = saIsRealNotificationsMode();
  const [notifs, setNotifs] = useStateNO(() => store ? store.getAll() : (OPERATIONAL_NOTIFICATIONS_MOCK || []));
  const [loading, setLoading] = useStateNO(() => useRealNotif && store?.isLoading?.());
  const [activeTab, setActiveTab] = useStateNO('inbox');
  const [inboxFilter, setInboxFilter] = useStateNO('all');
  const [error, setError] = useStateNO(() => (useRealNotif && store?.getLoadError?.()) || null);

  const refetch = async () => {
    if (!store?.reload) return;
    setLoading(true);
    setError(null);
    try {
      await store.reload();
    } catch (e) {
      setError(e?.message || 'Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  useEffectNO(() => {
    if (!store) return;
    const unsub = store.subscribe(updated => {
      setNotifs(updated);
      if (useRealNotif) {
        setLoading(store.isLoading?.() || false);
        setError(store.getLoadError?.() || null);
      }
    });
    return unsub;
  }, [useRealNotif]);

  const unread   = notifs.filter(n => !n._read && !n.read_at).length;
  const slaNotifs= notifs.filter(n => n.type === 'sla_ticket');
  const slaUnread= slaNotifs.filter(n => !n._read && !n.read_at).length;
  const critical = notifs.filter(n => n.severity === 'critical' || n.severity === 'security').length;
  const billing  = notifs.filter(n => n.type === 'billing').length;
  const prov     = notifs.filter(n => n.type === 'provisioning').length;
  const sec      = notifs.filter(n => n.type === 'security').length;
  const obs      = notifs.filter(n => n.type === 'observability').length;

  const KPIS = [
    { label: 'Não lidas',       value: unread,    color: 'var(--brand-navy)' },
    { label: 'SLA / Tickets',   value: slaNotifs.length, color: 'var(--danger)', highlight: slaUnread > 0 },
    { label: 'Críticas',        value: critical,  color: 'var(--danger)' },
    { label: 'Billing',         value: billing,   color: 'var(--warning)' },
    { label: 'Provisionamento', value: prov,      color: '#7c3aed' },
    { label: 'Segurança',       value: sec,       color: 'var(--danger)' },
  ];

  const TABS = [
    { id: 'inbox',     label: 'Inbox' },
    { id: 'criticas',  label: `Críticas (${critical})` },
    { id: 'regras',    label: 'Regras de alerta' },
    { id: 'historico', label: 'Histórico' },
    { id: 'prefs',     label: 'Preferências' },
  ].filter(t => !useRealNotif || (t.id !== 'historico' && t.id !== 'prefs'));

  useEffectNO(() => {
    if (!TABS.some(t => t.id === activeTab)) setActiveTab('inbox');
  }, [useRealNotif, activeTab]);

  const markAllRead = async () => {
    try {
      if (store) await store.markAllRead();
      else setNotifs(ns => ns.map(n => ({ ...n, _read: true, read_at: n.read_at || 'Agora' })));
      onToast('Todas as notificações marcadas como lidas');
    } catch (e) {
      onToast(e?.message || 'Erro ao marcar todas como lidas');
    }
  };

  const filterChips = [
    { id: 'all',         label: 'Todas' },
    { id: 'unread',      label: `Não lidas (${unread})` },
    { id: 'sla_ticket',  label: `SLA / Tickets${slaUnread > 0 ? ` (${slaUnread} 🔴)` : ` (${slaNotifs.length})`}` },
    { id: 'billing',     label: 'Billing' },
    { id: 'provisioning',label: 'Provisionamento' },
    { id: 'security',    label: 'Segurança' },
    { id: 'observability',label: 'Observabilidade' },
    { id: 'integration', label: 'Integrações' },
  ];

  return (
    <div className="content" style={{ maxWidth: 1200 }}>
      <div className="page-header">
        <div>
          <h1>Notificações Operacionais</h1>
          <p className="sub">Alertas internos da plataforma, tenants, billing, integrações e segurança.</p>
        </div>
        <div className="actions">
          {unread > 0 && <button className="btn" onClick={markAllRead}><Icon name="check" size={13} />Marcar todas como lidas</button>}
          <button className="btn" onClick={() => setActiveTab('regras')}><Icon name="settings" size={13} />Configurar regras</button>
          {!useRealNotif && (
            <button className="btn" onClick={() => onToast('Notificações exportadas')}><Icon name="download" size={13} />Exportar</button>
          )}
        </div>
      </div>

      {useRealNotif && error && (
        <div className="hint-card" style={{ marginBottom: 16, padding: 12, color: 'var(--danger)' }}>
          {error}
          <button type="button" className="btn sm" style={{ marginLeft: 12 }} onClick={refetch}>Tentar novamente</button>
        </div>
      )}
      {useRealNotif && loading && !error && (
        <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-muted)' }}>Carregando notificações…</div>
      )}

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 24 }}>
        {KPIS.map(k => (
          <div key={k.label} className="card" style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.color, letterSpacing: '-0.02em' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16, gap: 2 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400, color: activeTab === t.id ? 'var(--text)' : 'var(--text-muted)', borderBottom: activeTab === t.id ? '2px solid var(--brand-navy)' : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Inbox filter chips */}
      {(activeTab === 'inbox' || activeTab === 'criticas') && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
          {filterChips.map(f => (
            <button key={f.id} onClick={() => { setInboxFilter(f.id); setActiveTab('inbox'); }} style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: inboxFilter === f.id && activeTab === 'inbox' ? 'var(--brand-navy)' : 'var(--bg-subtle)', color: inboxFilter === f.id && activeTab === 'inbox' ? 'white' : 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loading && (
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody><SkeletonTable rows={5} cols={4} /></tbody></table>
        </div>
      )}
      {error && <ApiErrorState error={error} onRetry={refetch} />}
      {!loading && !error && (
        <>
          {(activeTab === 'inbox') && <TabInbox notifs={notifs} setNotifs={setNotifs} onToast={onToast} onNav={onNav} filter={inboxFilter} />}
          {activeTab === 'criticas' && <TabInbox notifs={notifs} setNotifs={setNotifs} onToast={onToast} onNav={onNav} filter="critical" />}
          {activeTab === 'regras'   && (
            useRealNotif
              ? <div className="hint-card" style={{ padding: 16 }}>Regras de alerta automáticas estão fora do MVP. As notificações exibidas são geradas a partir de eventos auditados no backend.</div>
              : <TabRegrasAlerta onToast={onToast} />
          )}
          {activeTab === 'historico'&& (
            <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: 'var(--bg-subtle)' }}>
                  <tr>{['Data/hora', 'Notificação', 'Status anterior', 'Status novo', 'Ator', 'Ações'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {notifs.filter(n => n.read_at || n.status === 'resolved').map(n => (
                    <tr key={n.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)' }}>{n.read_at || n.created_at}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 500, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</td>
                      <td style={{ padding: '10px 14px' }}><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Em aberto</span></td>
                      <td style={{ padding: '10px 14px' }}><span style={{ fontSize: 11, color: n.status === 'resolved' ? '#0f7a3e' : 'var(--text-muted)', fontWeight: 600 }}>{n.status === 'resolved' ? 'Resolvido' : 'Lido'}</span></td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)' }}>{n.assigned_to || 'Sistema'}</td>
                      <td style={{ padding: '10px 14px' }}><button className="icon-action" title="Ver" onClick={() => onToast('Detalhes — ' + n.title)}><Icon name="more" size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {activeTab === 'prefs' && <TabPreferencias onToast={onToast} />}
        </>
      )}
    </div>
  );
};

export {
  NotificacoesOperacionaisPage,
};
